import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AuthFrame from "./AuthFrame";
import { TitleBlock, Note, Actions, PrimaryBtn, SecondaryBtn, CodeInput } from "./pieces";
import { sendSignupCode, verifySignupCode } from "./authV2Services";
import { readSignup, writeSignup } from "./signupState";
import { routeAfterVerify } from "./signupFlow";
import { useIsPhone } from "../../ForBrands/useIsPhone";

// Why a code that was asked for by text went to the email instead
// (`fallback` from the send); any other reason is a text that failed.
const FALLBACK_NOTES = {
  sms_not_configured: "Text messages are not switched on yet, so the code went to your email.",
  email_owner_only: "This email already has an account on CACHE, so only a code sent to the email can confirm it.",
};
const fallbackNote = (fallback) => (fallback ? FALLBACK_NOTES[fallback] || "We could not text that number right now, so the code went to your email." : null);

// 03 · Verify code. Every typed email or mobile sign-up passes here, for
// every account type; Google identities arrive verified and skip it. The
// ticket the code earns is what /register/ requires. With a mobile number
// given, the code can be switched between the text and the email, unless
// only the email's owner can confirm it.
const SignupVerifyPage = () => {
  const navigate = useNavigate();
  const [state, setState] = useState(readSignup);
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [attemptsLeft, setAttemptsLeft] = useState(null);
  const [busy, setBusy] = useState(false);
  const [resendIn, setResendIn] = useState(state.resendInSeconds || 30);
  // This page renders AuthFrame itself, so it sits outside the phone theme
  // and reads the viewport directly.
  const onPhone = useIsPhone();

  useEffect(() => {
    if (state.ticket) navigate(routeAfterVerify(state), { replace: true });
    else if (!state.email) navigate("/signup/email", { replace: true });
  }, [state, navigate]);
  useEffect(() => {
    if (resendIn <= 0) return undefined;
    const t = setTimeout(() => setResendIn((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendIn]);

  const isSms = state.channel === "sms";
  const canText = !!state.phone && state.fallback !== "email_owner_only";
  const note = fallbackNote(state.fallback);
  const resend = async (channel) => {
    setBusy(true); setError("");
    try {
      const { ok, data } = await sendSignupCode({ email: state.email, phone: state.phone, channel });
      if (!ok) { setError(data?.message || "We could not resend the code."); return; }
      const next = writeSignup({ channel: data.channel, sentTo: data.to, fallback: data.fallback || null });
      setState(next); setCode(""); setAttemptsLeft(null); setResendIn(data.resendInSeconds || 30);
    } finally { setBusy(false); }
  };

  const verify = async () => {
    setBusy(true); setError("");
    try {
      const { ok, data } = await verifySignupCode({ email: state.email, code });
      if (!ok) {
        if (data?.error === "invalid_code") {
          setAttemptsLeft(data.attemptsLeft ?? null);
          setError(`That code is not right.${data.attemptsLeft != null ? ` You have ${data.attemptsLeft} attempt${data.attemptsLeft === 1 ? "" : "s"} left.` : ""}`);
        } else {
          setError(data?.message || "That code has expired. Request a new one.");
        }
        return;
      }
      // What the code proved: a text code proves the mobile, not the email.
      const next = writeSignup({ ticket: data.ticket, phone: data.phone || state.phone, emailVerified: !!data.emailVerified });
      navigate(routeAfterVerify(next));
    } catch {
      setError("We could not reach CACHE. Try again.");
    } finally { setBusy(false); }
  };

  const back = state.viaGoogle ? (state.accountType ? "/welcome" : "/register") : "/signup/email";
  const mm = String(Math.floor(resendIn / 60));
  const ss = String(resendIn % 60).padStart(2, "0");
  const sentLine = `Sent to ${state.sentTo || state.email}. It expires in 10 minutes.`;
  return (
    <AuthFrame title={isSms ? "Verify your number" : "Verify your email"} onBack={() => navigate(back)}>
      {/* On phones a long email breaks inside the column instead of
          running past the screen edge. */}
      <TitleBlock
        eyebrow="Verification"
        title="Enter the 6-digit code"
        sub={onPhone ? <span className="break-words">{sentLine}</span> : sentLine}
      />
      {note && <Note>{note}</Note>}
      <div className="flex flex-col gap-[10px]">
        <CodeInput value={code} onChange={(v) => { setCode(v); if (error) setError(""); }} error={!!error} />
        {error && <p className="bm-body-m text-bm-critical">{error}</p>}
      </div>
      <div className="flex items-center justify-between">
        <span className="bm-body-m text-bm-muted">Did not get it?</span>
        {resendIn > 0 && !error ? (
          <span className="bm-label-l text-bm-tertiary">Resend in {mm}:{ss}</span>
        ) : (
          <button type="button" onClick={() => resend(state.channel || "email")} disabled={busy} className="bm-label-l text-bm-orange hover:underline">Resend code</button>
        )}
      </div>
      {/* On phones the error line takes the note's place while it shows,
          as Figma 03a (1579:797) draws it. */}
      {!(onPhone && error) && (
        <Note>{state.viaGoogle ? "Wrong account? Go back and use another Google account or your email." : `Wrong ${isSms ? "number" : "email"}? Go back and edit it.`} Nothing is saved until you verify.</Note>
      )}
      <Actions>
        <PrimaryBtn onClick={verify} loading={busy} disabled={code.length !== 6 || attemptsLeft === 0}>Verify</PrimaryBtn>
        {isSms ? (
          <SecondaryBtn onClick={() => resend("email")} disabled={busy}>Send to my email instead</SecondaryBtn>
        ) : canText ? (
          <SecondaryBtn onClick={() => resend("sms")} disabled={busy}>Text it to my mobile instead</SecondaryBtn>
        ) : null}
      </Actions>
    </AuthFrame>
  );
};

export default SignupVerifyPage;
